<template>
    <div class="card mt-4" v-if="simulations">

        <div class="card-header pr-3">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.simulations') }}</h3>
                </div>
                <div class="col-6 text-right pr-0">
                    <actions-dropdown :actions="globalActions()" :large="true"></actions-dropdown>
                </div>
            </div>
        </div>

        <p class="card-body mt-2" v-if="empty">
            <strong>{{ $t('dashboard.no-simulation') }}</strong>
        </p>

        <div v-if="!empty">

            <el-table class="table-responsive table-flush"
                    :show-header="false"
                    :data="visibleSimulations">

                <el-table-column class-name="first-cell">
                    <template v-slot="{row}">

                        <span class="pin-icon" :style="{color: pinColor(row)}" @click.prevent="pinSimulation(row)">
                            <i class="fas fa-thumbtack"></i>
                        </span>
                        <span :class="'status-bullet text-'+simulationColor(row)" v-if="offlineSimulation(row.id)">
                            <i class="fas fa-circle"></i>
                        </span>
                        <h3 class="sim-title" :style="{'padding-left': offlineSimulation(row.id) ? '2rem' : '1.2rem'}">
                            <a href="#" @click.prevent="openSimulation(row)" v-if="$permissions.hasRight('dashboard.learners.read')">
                                {{ row.name }}
                            </a>
                            <span v-else>
                                {{ row.name }}
                            </span>
                            <small v-if="row.remedial">
                                <i class="fas fa-undo ml-1"></i>
                            </small>
                            <i class="fas fa-clipboard-check ml-1" v-if="row.assessment"></i>
                        </h3>

                    </template>
                </el-table-column>

                <el-table-column width="130">
                    <template v-slot="{row}">

                        <base-progress type="info" :value="progress(row)">
                        </base-progress>

                        <span class="d-block">
                            <strong>{{ progress(row) }}%</strong> {{ $t('dashboard.done').toLowerCase() }}
                        </span>

                    </template>
                </el-table-column>

                <el-table-column align="right">
                    <template v-slot="{row}">
                        <div class="pt-1 pr-1 pb-1">

                            <base-button type="secondary" size="sm" class="mb-1 ml-1 mr-0" @click="openPilotSummary(row)"
                                v-if="!row.remedial && !row.assessment && $permissions.hasRight('dashboard.pilot-log.access')"
                            >
                                <i class="fas fa-pencil-alt mr-1"></i> {{ $t('common.issues') }}
                            </base-button>

                            <base-button type="secondary" size="sm" class="mb-1 ml-1 mr-0" @click="openLog(row)"
                                v-if="!row.remedial && !row.assessment && $permissions.hasRight('dashboard.passive-log.access') && !$permissions.hasRight('dashboard.pilot-log.access')"
                            >
                                {{ $t('common.log') }}
                            </base-button>

                            <base-button type="primary" size="sm" class="mb-1 ml-1 mr-0" @click="openPilotLog(row)"
                                v-if="!row.remedial && !row.assessment && $permissions.hasRight('dashboard.pilot-log.access')"
                            >
                                <i class="fas fa-play mr-1"></i> {{ $t('common.log') }}
                            </base-button>

                            <base-button type="primary" size="sm" class="mb-1 ml-1 mr-0" @click="openSimulation(row)"
                                v-if="$permissions.hasRight('dashboard.sessions.read')"
                            >
                                {{ $t('dashboard.more') }}
                            </base-button>

                        </div>
                    </template>
                </el-table-column>

                <el-table-column align="right" width="30" class-name="actions">
                    <template v-slot="{row}">
                        <div class="table-actions">
                            <actions-dropdown :actions="actions(row)" :large="true"></actions-dropdown>
                        </div>
                    </template>
                </el-table-column>
                
            </el-table>
        </div>

        <portal to="modals">

            <confirm-modal v-model="modals.offlineConfirmRemove">
                <p>{{ $t('pwa.remove-confirm') }}</p>
            </confirm-modal>

            <sync-modal v-model="modals.offlineSync" :sync="sync" @done="syncDone">
            </sync-modal>

            <base-modal
                v-model="modals.offlineError"
                size="sm"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ offlineErrorMessage }}</h4>
                </div>
            </base-modal>

            <base-modal
                v-model="modals.offlineConflict"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ $t('common.confirm-question') }}</h4>
                    <p>{{ $t('pwa.http-error-409') }}</p>
                </div>

                <template slot="footer">
                    <div class="text-center">
                        <base-button type="white" @click="confirmDownload">{{ $t('common.confirm') }}</base-button>
                        <base-button type="link" class="text-white" @click="cancelDownload">{{ $t('common.cancel') }}</base-button>
                    </div>
                </template>
            </base-modal>

        </portal>

    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import SyncModal from '../offline/SyncModal'

    export default {

        components: {
            SyncModal
        },

        props: {

            registration: {
                type: Object,
                required: true
            },

            simulations: {
                type: Array,
                required: true
            }
        },
        
        data() {
            return {
                modals: new ModalsController(['offlineError', 'offlineConflict', 'offlineConfirmRemove', 'offlineSync']),
                currentId: null,
                offlineErrorMessage: null,
                offlineSimulations: [],
                offlineEndpoint: '/astral/api/front/offline/logs',
                sync: {
                    endpoint: '/astral/api/front/offline/logs',
                    params: {
                        registration_id: this.registration.id,
                    },
                },
                downloadingBatch: false,
                forceDownload: false,
                pinned: this.$auth.getUiConfig('pinned_simulation_in_registration_' + this.registration.id),
            }
        },

        created() {
            this.modals.init(this, 'modals')
            this.loadOfflineStatus()
        },

        computed: {

            empty() {
                return this.visibleSimulations.length == 0
            },

            downloadableSims() {
                return this.visibleSimulations.filter(sim => !this.offlineSimulation(sim.id) && !sim.remedial && !sim.assessment)
            },

            visibleSimulations() {
                let visibleSimulations = this.simulations.filter(sim => !sim.private || this.$permissions.hasRight('dashboard.learners.write'))
                if (!this.pinned) {
                    return visibleSimulations
                }
                let visible = false;
                let pinnedSimulations = [];
                for (const simulation of visibleSimulations) {
                    if (simulation.id == this.pinned) {
                        visible = true;
                    }
                    if (visible) {
                        pinnedSimulations.push(simulation)
                    }
                }
                return pinnedSimulations
            }
        },

        methods: {

            offlineSimulation(simulationId) {
                return this.offlineSimulations.find(candidate => candidate.simulation.id == simulationId)
            },

            simulationColor(simulation) {
                return this.offlineSimulation(simulation.id).sync.color
            },

            pinColor(simulation) {
                return this.pinned == simulation.id ? '#32325d' : '#cccccc';
            },

            actions(simulation) {
                let actions = []

                if (!simulation.remedial && !simulation.assessment && this.$permissions.hasRight('dashboard.issues.read-all')) {
                    actions.push({
                        label: this.$t('common.manage-issues'),
                        handler: this.openIssues,
                        data: simulation
                    })
                }                
                if (this.offlineSimulation(simulation.id)) {
                    if (this.simulationColor(simulation) == 'warning') {
                        actions.push({
                            label: this.$t('pwa.refresh'),
                            handler: this.downloadSingleLog,
                            data: simulation.id
                        })
                    }
                    if (this.$permissions.hasRight('dashboard.pilot-log.access')) {
                        if (this.simulationColor(simulation) == 'danger') {
                            actions.push({
                                label: this.$t('pwa.sync'),
                                handler: this.syncLog,
                                data: simulation.id
                            })
                        }
                    }
                    actions.push({
                        label: this.$t('pwa.remove'),
                        handler: this.removeLog,
                        data: simulation.id
                    })
                    if (this.$permissions.hasRight('dashboard.pilot-log.access')) {
                        if (this.simulationColor(simulation) != 'danger') {
                            actions.push({
                                label: this.$t('pwa.resync'),
                                handler: this.resyncLog,
                                data: simulation.id
                            })
                        }
                    }
                } else {
                    if (!simulation.remedial && !simulation.assessment &&
                        (this.$permissions.hasRight('dashboard.learners.write')
                        || this.$permissions.hasRight('dashboard.pilot-log.access'))) {

                        actions.push({
                            label: this.$t('pwa.download'),
                            handler: this.downloadSingleLog,
                            data: simulation.id
                        })
                    }
                }
                if (!simulation.assessment && this.$permissions.hasRight('dashboard.internal-reports.read')) {
                    actions.push({
                        label: this.$t('reports.learners-competencies'),
                        handler: this.openCompetenciesReport,
                        data: simulation,
                        icon: 'tim-icons icon-line-chart',
                        divided: 'true'
                    })
                }                
                return actions
            },

            globalActions() {
                let actions = []

                if (this.$permissions.hasRight('dashboard.learners.write')
                    || this.$permissions.hasRight('dashboard.pilot-log.access')) {

                    actions.push({
                        label: this.$t('pwa.download-all'),
                        handler: this.downloadBatchOfLogs
                    })
                }
                return actions
            },

            downloadSingleLog(simulationId) {
                this.downloadingBatch = false
                this.forceDownload = false
                this.downloadLog(simulationId)
            },

            downloadBatchOfLogs(first = true) {
                if (first) {
                    this.downloadingBatch = true
                    this.forceDownload = false
                }
                if (!this.downloadableSims.length == 0) {
                    this.downloadLog(this.downloadableSims[0].id)
                }
            },

            downloadLog(simulationId) {
                this.currentId = simulationId
                this.$loader.show()
                axios.post(this.offlineEndpoint, {
                    registration_id: this.registration.id,
                    simulation_id: simulationId,
                    force: this.forceDownload
                })
                .then(resp => {
                    this.$loader.hide()
                    this.refreshOfflineStatus(resp.data)
                    if (this.downloadingBatch) {
                        this.downloadBatchOfLogs(false)
                    }
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            confirmDownload() {
                this.modals.offlineConflict.show = false
                this.forceDownload = true
                this.downloadLog(this.currentId)
            },

            cancelDownload() {
                this.modals.offlineConflict.show = false
            },

            removeLog(simulationId) {
                this.modals.open('offlineConfirmRemove').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        axios.delete(this.offlineEndpoint, { data: {
                            registration_id: this.registration.id,
                            simulation_id: simulationId,
                        }})
                        .then(resp => {
                            this.refreshOfflineStatus(resp.data)
                        }).catch(this.handleOfflineError)
                    }
                })
            },

            syncLog(id, force = false) {
                this.sync.params.simulation_id = id
                this.sync.params.force = force
                this.modals.open('offlineSync')
            },

            resyncLog(id) {
                this.syncLog(id, true)
            },

            syncDone() {
                this.loadOfflineStatus()
            },

            handleOfflineError(error) {
                if (!error.response) {
                    return
                }
                switch(error.response.status) {
                    case 409:
                        this.modals.open('offlineConflict')
                        break
                    case 403:
                    case 404:
                    case 405:
                    case 444:
                        this.offlineErrorMessage = this.$t('pwa.http-error-' + error.response.status)
                        this.modals.open('offlineError')
                        break
                    default:
                        this.$auth.handleErrorVm(error, this)
                }
            },

            loadOfflineStatus() {
                axios.get(this.offlineEndpoint)
                .then(resp => {
                    this.refreshOfflineStatus(resp.data)
                })
            },

            refreshOfflineStatus(responseData) {
                this.offlineSimulations = responseData.data
                    .filter(item => item.registration.id == this.registration.id)
            },

            progress(simulation) {
                if (simulation.state.sessions_count == 0) {
                    return 0
                }
                let progress = (simulation.state.completed_sessions ? simulation.state.completed_sessions : 0) * 100 / simulation.state.sessions_count
                return parseInt(progress)
            },

            openLog(simulation) {
                this.$router.push({
                    name: 'log',
                    params: {
                        registrationId: this.$route.params.registrationId,
                        simulationId: simulation.id,
                        groupId: this.$route.params.groupId ? this.$route.params.groupId : 0,
                    }
                });
            },

            openPilotLog(simulation) {
                this.$router.push({
                    name: 'log-simulation-log',
                    params: {
                        registrationId: this.$route.params.registrationId,
                        simulationId: simulation.id,
                        groupId: this.$route.params.groupId ? this.$route.params.groupId : 0,
                    }
                });
            },

            openPilotSummary(simulation) {
                this.$router.push({
                    name: 'log-simulation-summary',
                    params: {
                        registrationId: this.$route.params.registrationId,
                        simulationId: simulation.id,
                        groupId: this.$route.params.groupId ? this.$route.params.groupId : 0,
                    }
                });
            },

            openIssues(simulation) {
                this.$router.push({ name: 'log-simulation-issues',
                    params: { 
                        registrationId: this.$route.params.registrationId,
                        simulationId: simulation.id,
                        groupId: this.$route.params.groupId ? this.$route.params.groupId : 0,
                    }
                })
            },

            openSimulation(simulation) {
                if (this.$route.params.groupId && this.$route.params.groupId != 0) {  // Important.
                    this.$router.push({
                        name: 'dashboard-group-simulation',
                        params: {
                            groupId: this.$route.params.groupId,
                            registrationId: this.$route.params.registrationId,
                            simulationId: simulation.id,
                        }
                    });
                } else {
                    this.$router.push({
                        name: 'dashboard-cohort-simulation',
                        params: {
                            cohortId: this.$route.params.cohortId,
                            registrationId: this.$route.params.registrationId,
                            simulationId: simulation.id,
                        }
                    });
                }
            },

            pinSimulation(simulation) {
                this.pinned = this.pinned == simulation.id ? null : simulation.id
                this.$auth.setUiConfig(this.pinned, 'pinned_simulation_in_registration_' + this.registration.id);
            },

            openCompetenciesReport(simulation) {
                if (this.$route.params.groupId && this.$route.params.groupId != 0) {  // Important.
                    this.$router.push({
                        name: 'dashboard-group-simulation-competencies',
                        params: {
                            groupId: this.$route.params.groupId,
                            registrationId: this.$route.params.registrationId,
                            simulationId: simulation.id,
                        }
                    });
                } else {
                    this.$router.push({
                        name: 'dashboard-cohort-simulation-competencies',
                        params: {
                            cohortId: this.$route.params.cohortId,
                            registrationId: this.$route.params.registrationId,
                            simulationId: simulation.id,
                        }
                    });
                }
            },
        }
    }
</script>

<style scoped>

    .card >>> td {
        padding-right: 0;
        font-size: 0.8rem;
    }
    .card >>> td.first-cell {
        padding-left: 0.5rem;
    }
    .card >>> td.actions {
        padding-left: 0;
        padding-right: 0.5rem;
    }
    .text-warning {
        color: #fbb340 !important;
    }
    .pin-icon {
        margin-left: 0;
        float: left !important;
        position: absolute;
        margin-top: 1px;
        cursor: pointer;
    }
    .status-bullet {
        font-size: 8px;
        margin-left: 1.2rem;
        float: left !important;
    }
    .sim-title {
        margin-bottom: 0;
    }

</style>